import React, { useState, useEffect } from "react";
import useRealtimeDraft from '../../../realtime/useRealtimeDraft';
import useTrackedRealtimeEdits from '../../../realtime/useTrackedRealtimeEdits';
import { useSelector, useDispatch } from "react-redux";
import { selectUserDetails, selectUserRoles } from "../../../redux/slices/userSlice";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { selectJobChangeOrderDetails, selectJobId, selectChangeOrderParent, selectGeneralProject } from "../../../redux/selectors";
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { patchData } from '../../../redux/slices/apiCallSlice';
import useSendEmail from '../../../functions/useSendEmail';
import useUserContact from '../../../functions/useUserContact';
import { formatDateForDisplay, formatDateForEmail } from "../../../utils/dateUtils";

// CC recipients for all task notification emails
const CC_RECIPIENTS = ["todd.blackburn@watertronics.com", "ryan.nelson@watertronics.com"];

const ChangeOrderTasks = () => {
    const jobId = useSelector(selectJobId);
    const userDetails = useSelector(selectUserDetails);
    const currentUser = userDetails?.name;
    const userRoles = useSelector(selectUserRoles);
    const [roles, setRoles] = useState(mapUserRoles(userRoles));
    const changeOrderDetails = useSelector(selectJobChangeOrderDetails); // Use job-specific selector from fullJobFetchSlice
    const changeOrderParent = useSelector(selectChangeOrderParent);
    const projectDetails = useSelector(selectGeneralProject);
    const dispatch = useDispatch();

    const sendEmail = useSendEmail();
    const { userContacts, loading } = useUserContact();
    const [sendingEmails, setSendingEmails] = useState(false);

    // State for task assignment feature
    const [showAddTaskModal, setShowAddTaskModal] = useState(false);
    const [selectedChangeOrder, setSelectedChangeOrder] = useState('');
    const [availableChanges, setAvailableChanges] = useState([]);
    const [selectedChanges, setSelectedChanges] = useState([]);
    const [assignmentData, setAssignmentData] = useState({
        mechanical: false,
        electrical: false,
        controls: false,
        csm: false
    });
    const [filteredChangeOrders, setFilteredChangeOrders] = useState([]);

    // State for email preview and self-testing
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const realtimeEdits = useTrackedRealtimeEdits();
    useRealtimeDraft(showAddTaskModal || isPreviewMode || sendingEmails);
    const [selfTestMode, setSelfTestMode] = useState(false);
    const [emailPreview, setEmailPreview] = useState({
        recipients: [],
        subject: "",
        body: ""
    });    // State for existing task management
    const [localChangeOrderDetails, setLocalChangeOrderDetails] = useState([]);
    const [showCompletedTasks, setShowCompletedTasks] = useState(false);
    const [deletedItemsIds,] = useState(new Set());

    useEffect(() => {
        setRoles(mapUserRoles(userRoles));
    }, [userRoles]);

    useEffect(() => {
        const expandedDetails = changeOrderDetails.flatMap(detail => {
            // Skip any deleted items
            if (deletedItemsIds && deletedItemsIds.has(detail.ID)) {
                return [];
            }

            const details = [];
            if (detail.ActionRequiredMechanical) details.push({ ...detail, actionType: 'Mechanical' });
            if (detail.ActionRequiredElectrical) details.push({ ...detail, actionType: 'Electrical' });
            if (detail.ActionRequiredControls) details.push({ ...detail, actionType: 'Controls' });
            if (detail.ActionRequiredCSM) details.push({ ...detail, actionType: 'CSM' });
            return details;
        });

        const filteredDetails = expandedDetails.filter(detail => {
            // Get the acknowledged and completed fields based on the action type
            let acknowledged = false;
            let completed = false;

            if (detail.actionType === 'Mechanical') {
                acknowledged = detail.MechanicalAcknowledged === "true" || detail.MechanicalAcknowledged === true;
                completed = detail.MechanicalCompleted === "true" || detail.MechanicalCompleted === true;
            } else if (detail.actionType === 'Electrical') {
                acknowledged = detail.ElectricalAcknowledged === "true" || detail.ElectricalAcknowledged === true;
                completed = detail.ElectricalCompleted === "true" || detail.ElectricalCompleted === true;
            } else if (detail.actionType === 'Controls') {
                acknowledged = detail.ControlsAcknowledged === "true" || detail.ControlsAcknowledged === true;
                completed = detail.ControlsCompleted === "true" || detail.ControlsCompleted === true;
            } else if (detail.actionType === 'CSM') {
                acknowledged = detail.CSMAcknowledged === "true" || detail.CSMAcknowledged === true;
                completed = detail.CSMCompleted === "true" || detail.CSMCompleted === true;
            }
              // Skip tasks that are both acknowledged and completed (unless showing completed tasks)
            if (acknowledged && completed && !showCompletedTasks) {
                return false;
            }

            return detail.ProjectID === jobId;
        });

        // Sort tasks in descending order by change order number (most recent first)
        filteredDetails.sort((a, b) => {
            // Convert to number for proper comparison or use 0 if null/undefined
            const numA = a.ChangeOrderNumber ? parseInt(a.ChangeOrderNumber) : 0;
            const numB = b.ChangeOrderNumber ? parseInt(b.ChangeOrderNumber) : 0;

            // Primary sort by change order number (descending)
            if (numB !== numA) {
                return numB - numA;
            }

            // Secondary sort by actionType if change order numbers are the same
            const actionTypes = ['Mechanical', 'Electrical', 'Controls', 'CSM'];
            return actionTypes.indexOf(a.actionType) - actionTypes.indexOf(b.actionType);
        });
          setLocalChangeOrderDetails(filteredDetails);
    }, [changeOrderDetails, jobId, showCompletedTasks, deletedItemsIds]);

    useEffect(() => {
        // Filter only change orders for this job and sort by most recent first
        const filtered = changeOrderParent.filter(order => order.ProjectID === jobId);
        filtered.sort((a, b) => b.ChangeOrderNumber - a.ChangeOrderNumber);
        setFilteredChangeOrders(filtered);
    }, [changeOrderParent, jobId]);

    // When a change order is selected, get its changes
    useEffect(() => {
        if (selectedChangeOrder) {
            const changes = changeOrderDetails.filter(detail =>
                detail.ChangeOrderNumber === parseInt(selectedChangeOrder) &&
                detail.ProjectID === jobId
            );
            setAvailableChanges(changes);
        } else {
            setAvailableChanges([]);
        }
        // Reset selected changes when change order changes
        setSelectedChanges([]);
    }, [selectedChangeOrder, changeOrderDetails, jobId]);

    const handleCheckboxChange = (detailId, field, value) => {
        const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
        const data = { [field]: String(value), ID: detailId };

        realtimeEdits.track(data.ID, data, dispatch(patchData({ url, data })));

        setLocalChangeOrderDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.ID === detailId ? { ...detail, [field]: value } : detail
            )
        );
    };

    const handleInputChange = (detailId, field, value) => {
        const url = `${process.env.REACT_APP_API_URL}/change-order-details`;
        const data = { [field]: value, ID: detailId };

        realtimeEdits.track(data.ID, data, dispatch(patchData({ url, data })));

        setLocalChangeOrderDetails(prevDetails =>
            prevDetails.map(detail =>
                detail.ID === detailId ? { ...detail, [field]: value } : detail
            )
        );
    };

    const formatDate = (dateString) => {
        return formatDateForEmail(dateString);
    };    const getAssignedPerson = (detail) => {
        if (detail.actionType === 'Mechanical') return projectDetails[0]?.MechanicalEngineer || "Mechanical Engineering";
        if (detail.actionType === 'Electrical') return projectDetails[0]?.ElectricalEngineer || "Electrical Engineering";
        if (detail.actionType === 'Controls') return projectDetails[0]?.ControlsEngineer || "Controls Engineering";
        if (detail.actionType === 'CSM') return "David Neesen";
        return "";
    };

    const getAcknowledgedField = (detail) => {
        if (detail.actionType === 'Mechanical') return 'MechanicalAcknowledged';
        if (detail.actionType === 'Electrical') return 'ElectricalAcknowledged';
        if (detail.actionType === 'Controls') return 'ControlsAcknowledged';
        if (detail.actionType === 'CSM') return 'CSMAcknowledged';
        return '';
    };

    const getCompletedField = (detail) => {
        if (detail.actionType === 'Mechanical') return 'MechanicalCompleted';
        if (detail.actionType === 'Electrical') return 'ElectricalCompleted';
        if (detail.actionType === 'Controls') return 'ControlsCompleted';
        if (detail.actionType === 'CSM') return 'CSMCompleted';
        return '';
    };

    const getColorBarStyle = (acknowledged, completed) => {
        if (acknowledged && completed) return { backgroundColor: 'green' };
        if (acknowledged && !completed) return { backgroundColor: 'yellow' };
        return { backgroundColor: 'red' };
    };

    // Function to check if the current user has permission to modify a specific task
    const canModifyTask = (detail) => {
        // Admin users can always modify
        if (roles.Admin) return true;

        // Check if the current user is the assigned person for this task
        const assignedPerson = getAssignedPerson(detail);
        return assignedPerson === currentUser;
    };

    const handleShowAddTaskModal = () => {
        setShowAddTaskModal(true);
        // Reset form state
        setSelectedChangeOrder('');
        setSelectedChanges([]);
        setAssignmentData({
            mechanical: false,
            electrical: false,
            controls: false,
            csm: false
        });
    };

    const handleCloseAddTaskModal = () => {
        setShowAddTaskModal(false);
    };

    const handleChangeOrderSelect = (e) => {
        setSelectedChangeOrder(e.target.value);
    };

    const handleChangeSelect = (e, changeId) => {
        const isChecked = e.target.checked;

        if (isChecked) {
            setSelectedChanges(prev => [...prev, changeId]);
        } else {
            setSelectedChanges(prev => prev.filter(id => id !== changeId));
        }
    };

    const handleAssignmentChange = (field, value) => {
        setAssignmentData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAssignTasks = async () => {
        // Ensure at least one department is selected
        if (!assignmentData.mechanical && !assignmentData.electrical && !assignmentData.controls && !assignmentData.csm) {
            alert("Please select at least one department to assign tasks to");
            return;
        }

        // Ensure at least one change is selected
        if (selectedChanges.length === 0) {
            alert("Please select at least one change to assign");
            return;
        }

        try {
            setSendingEmails(true);
            const url = `${process.env.REACT_APP_API_URL}/change-order-details`;

            // Create update data for each selected change
            for (const changeId of selectedChanges) {
                const updateData = {
                    ID: changeId,
                    ActionRequiredMechanical: assignmentData.mechanical ? "true" : "false",
                    ActionRequiredElectrical: assignmentData.electrical ? "true" : "false",
                    ActionRequiredControls: assignmentData.controls ? "true" : "false",
                    ActionRequiredCSM: assignmentData.csm ? "true" : "false"
                };

                // Assign the appropriate engineers based on project details
                if (assignmentData.mechanical) {
                    updateData.AssignedMechanical = projectDetails[0]?.MechanicalEngineer || null;
                }
                if (assignmentData.electrical) {
                    updateData.AssignedElectrical = projectDetails[0]?.ElectricalEngineer || null;
                }                if (assignmentData.controls) {
                    updateData.AssignedControls = projectDetails[0]?.ControlsEngineer || null;
                }
                if (assignmentData.csm) {
                    updateData.AssignedNP = "david.neesen@watertronics.com";
                }

                await dispatch(patchData({ url, data: updateData }));
            }

            // Send task notifications
            await sendTaskNotifications(selectedChangeOrder, selectedChanges);

            // Close both modals
            setShowAddTaskModal(false);
            setIsPreviewMode(false);
        } catch (error) {
            console.error("Error assigning tasks:", error);
            alert("There was an error assigning tasks. Please try again.");
        } finally {
            setSendingEmails(false);
        }
    };    // Function to send notification emails to assigned users
    const sendTaskNotifications = async (selectedChangeOrderNum, selectedChangesData) => {
        if (loading || !userContacts) return;

        try {
            setSendingEmails(true);

            // Get the change order details
            const changeOrder = filteredChangeOrders.find(order =>
                order.ChangeOrderNumber.toString() === selectedChangeOrderNum.toString()
            );

            if (!changeOrder) return;

            // Get the changes that need to be assigned
            const changesDetails = selectedChangesData.map(changeId =>
                availableChanges.find(change => change.ID === changeId)
            ).filter(Boolean);

            // Structure for recipients grouped by department
            const notifications = {
                mechanical: {
                    assignee: projectDetails[0]?.MechanicalEngineer,
                    email: null,
                    tasks: []
                },
                electrical: {
                    assignee: projectDetails[0]?.ElectricalEngineer,
                    email: null,
                    tasks: []
                },
                controls: {
                    assignee: projectDetails[0]?.ControlsEngineer,
                    email: null,
                    tasks: []
                },
                csm: {
                    assignee: "David Neesen",
                    email: "david.neesen@watertronics.com",
                    tasks: []
                }
            };

            // Populate tasks for each department
            if (assignmentData.mechanical && notifications.mechanical.assignee) {
                notifications.mechanical.tasks = changesDetails;
            }

            if (assignmentData.electrical && notifications.electrical.assignee) {
                notifications.electrical.tasks = changesDetails;
            }

            if (assignmentData.controls && notifications.controls.assignee) {
                notifications.controls.tasks = changesDetails;
            }

            if (assignmentData.csm) {
                notifications.csm.tasks = changesDetails;
            }

            // Find emails for engineers from userContacts
            if (userContacts.mechanicalEngineer) {
                const mechanicalContact = userContacts.mechanicalEngineer.find(
                    contact => contact.name === notifications.mechanical.assignee
                );
                if (mechanicalContact) {
                    notifications.mechanical.email = mechanicalContact.email;
                }
            }

            if (userContacts.electricalEngineer) {
                const electricalContact = userContacts.electricalEngineer.find(
                    contact => contact.name === notifications.electrical.assignee
                );
                if (electricalContact) {
                    notifications.electrical.email = electricalContact.email;
                }
            }

            if (userContacts.controlsEngineer) {
                const controlsContact = userContacts.controlsEngineer.find(
                    contact => contact.name === notifications.controls.assignee
                );
                if (controlsContact) {
                    notifications.controls.email = controlsContact.email;
                }
            }

            // Send email to each department with tasks
            for (const [, data] of Object.entries(notifications)) {
                if (data.tasks.length > 0 && data.email) {
                    const emailSubject = `New Task Assignment for CO# ${changeOrder.ChangeOrderNumber} - ${projectDetails[0]?.JobNumber} - ${projectDetails[0]?.ProjectName}`;

                    let emailBody = `
                        <html>
                            <body>
                                <h2>New Task Assignment</h2>
                                <p>You have been assigned new tasks for:</p>
                                <p><strong>Change Order #${changeOrder.ChangeOrderNumber}</strong> - ${projectDetails[0]?.JobNumber} - ${projectDetails[0]?.ProjectName}</p>
                                <p><strong>Written By:</strong> ${changeOrder.WrittenBy} on ${formatDateForDisplay(changeOrder.DateSubmitted)}</p>
                                <p>Please review and acknowledge the following tasks:</p>
                                <ul>
                    `;

                    data.tasks.forEach(task => {
                        emailBody += `
                            <li>
                                <strong>${task.Title || "Untitled Task"}</strong>
                                <div>Changed from: ${task.ExistingValue || "BLANK"} to: ${task.NewValue || "BLANK"}</div>
                            </li>
                        `;
                    });

                    emailBody += `
                                </ul>
                                <p>Please review Change Order Tasks in the <a href="https://portal.watertronics.com">Production Portal</a> for more information.</p>
                                <p>This task was retroactively assigned by ${currentUser}.</p>
                            </body>
                        </html>
                    `;

                    await sendEmail({
                        subject: emailSubject,
                        body: emailBody,
                        toRecipients: [data.email],
                        ccRecipients: CC_RECIPIENTS,
                        from: "change.order@watertronics.com"
                    });
                }
            }
        } catch (error) {
            console.error("Error sending task notifications:", error);
        } finally {
            setSendingEmails(false);
        }
    };

    // Function to generate email preview
    const generateEmailPreview = async () => {
        // Skip if no change order is selected
        if (!selectedChangeOrder || selectedChanges.length === 0 || loading || !userContacts) {
            return;
        }

        // Get the change order details
        const changeOrder = filteredChangeOrders.find(order =>
            order.ChangeOrderNumber.toString() === selectedChangeOrder.toString()
        );

        if (!changeOrder) return;

        // Get the changes that need to be assigned
        const changesDetails = selectedChanges.map(changeId =>
            availableChanges.find(change => change.ID === changeId)
        ).filter(Boolean);
          // Structure for recipients grouped by department
        const notifications = {
            mechanical: {
                assignee: projectDetails[0]?.MechanicalEngineer,
                email: null,
                tasks: []
            },
            electrical: {
                assignee: projectDetails[0]?.ElectricalEngineer,
                email: null,
                tasks: []
            },
            controls: {
                assignee: projectDetails[0]?.ControlsEngineer,
                email: null,
                tasks: []
            },
            csm: {
                assignee: "David Neesen",
                email: "david.neesen@watertronics.com",
                tasks: []
            }
        };

        // Populate tasks for each department
        if (assignmentData.mechanical && notifications.mechanical.assignee) {
            notifications.mechanical.tasks = changesDetails;
        }

        if (assignmentData.electrical && notifications.electrical.assignee) {
            notifications.electrical.tasks = changesDetails;
        }

        if (assignmentData.controls && notifications.controls.assignee) {
            notifications.controls.tasks = changesDetails;
        }

        if (assignmentData.csm) {
            notifications.csm.tasks = changesDetails;
        }

        // Find emails for engineers from userContacts
        if (userContacts.mechanicalEngineer) {
            const mechanicalContact = userContacts.mechanicalEngineer.find(
                contact => contact.name === notifications.mechanical.assignee
            );
            if (mechanicalContact) {
                notifications.mechanical.email = mechanicalContact.email;
            }
        }

        if (userContacts.electricalEngineer) {
            const electricalContact = userContacts.electricalEngineer.find(
                contact => contact.name === notifications.electrical.assignee
            );
            if (electricalContact) {
                notifications.electrical.email = electricalContact.email;
            }
        }

        if (userContacts.controlsEngineer) {
            const controlsContact = userContacts.controlsEngineer.find(
                contact => contact.name === notifications.controls.assignee
            );
            if (controlsContact) {
                notifications.controls.email = controlsContact.email;
            }
        }

        // Collect all recipients that would receive emails
        const recipients = [];

        // If self-test mode is enabled, only use admin's email
        if (selfTestMode && userDetails?.username) {
            recipients.push({
                name: userDetails.name,
                email: userDetails.username,
                department: "Self-Test (Admin only)"
            });
        } else {
            // Otherwise collect all appropriate recipients
            for (const [dept, data] of Object.entries(notifications)) {
                if (data.tasks.length > 0 && data.email) {
                    recipients.push({
                        name: data.assignee,
                        email: data.email,
                        department: dept.charAt(0).toUpperCase() + dept.slice(1)
                    });
                }
            }
        }

        // Prepare email preview
        const emailSubject = `New Task Assignment for CO# ${changeOrder.ChangeOrderNumber} - ${projectDetails[0]?.JobNumber} - ${projectDetails[0]?.ProjectName}`;

        let emailBody = `
            <html>
                <body>
                    <h2>New Task Assignment</h2>
                    <p>You have been assigned new tasks for:</p>
                    <p><strong>Change Order #${changeOrder.ChangeOrderNumber}</strong> - ${projectDetails[0]?.JobNumber} - ${projectDetails[0]?.ProjectName}</p>
                    <p><strong>Written By:</strong> ${changeOrder.WrittenBy} on ${formatDateForDisplay(changeOrder.DateSubmitted)}</p>
                    <p>Please review and acknowledge the following tasks:</p>
                    <ul>
        `;

        changesDetails.forEach(task => {
            emailBody += `
                <li>
                    <strong>${task.Title || "Untitled Task"}</strong>
                    <div>Changed from: ${task.ExistingValue || "BLANK"} to: ${task.NewValue || "BLANK"}</div>
                </li>
            `;
        });

        emailBody += `
                    </ul>
                    <p>Please review Change Order Tasks in the <a href="https://portal.watertronics.com">Production Portal</a> for more information.</p>
                    <p>This task was retroactively assigned by ${currentUser}.</p>
                </body>
            </html>
        `;

        setEmailPreview({
            recipients,
            subject: emailSubject,
            body: emailBody
        });

        setIsPreviewMode(true);
    };

    return (
        <Container className="change-order-tasks">
            <h2>Change Order Tasks</h2>
              {/* Add Task button - only visible to Admin or Apps roles */}
            {(roles.Admin || roles.Apps) && (
                <Button
                    variant="primary"
                    className="mb-3"
                    onClick={handleShowAddTaskModal}
                >
                    Assign Tasks for Existing Change Order
                </Button>
            )}
              {/* Filter toggle for showing completed tasks */}
            <div className="mb-3 d-flex align-items-center change-order-task-filter">
                <Form.Check
                    type="checkbox"
                    id="show-completed-tasks"
                    label="Show completed tasks"
                    checked={showCompletedTasks}
                    onChange={(e) => setShowCompletedTasks(e.target.checked)}
                    className="me-3"
                />
                <small className="text-muted">
                    {showCompletedTasks ?
                        `Showing all tasks (${localChangeOrderDetails.length} total)` :
                        `Showing active tasks only (${localChangeOrderDetails.length} tasks)`
                    }
                </small>
            </div>

            {/* Task assignment modal */}
            <Modal show={showAddTaskModal} onHide={handleCloseAddTaskModal} size="lg" className="change-order-modal change-order-task-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Assign Tasks for Existing Change Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Change Order</Form.Label>
                            <Form.Control
                                as="select"
                                value={selectedChangeOrder}
                                onChange={handleChangeOrderSelect}
                            >
                                <option value="">Select a Change Order</option>
                                {filteredChangeOrders.map(order => (
                                    <option key={order.ID} value={order.ChangeOrderNumber}>
                                        CO# {order.ChangeOrderNumber} - {formatDateForDisplay(order.DateSubmitted)}
                                    </option>
                                ))}
                            </Form.Control>
                        </Form.Group>

                        {selectedChangeOrder && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>Select Changes to Assign Tasks For</Form.Label>
                                    {availableChanges.length > 0 ? (
                                        <div className="border p-3 change-order-task-change-list">
                                            {/* Group changes by reason */}
                                            {Object.entries(
                                                availableChanges.reduce((groups, change) => {
                                                    const reason = change.ReasonForChange || "Other";
                                                    if (!groups[reason]) groups[reason] = [];
                                                    groups[reason].push(change);
                                                    return groups;
                                                }, {})
                                            ).map(([reason, changes]) => (
                                                <div key={reason} className="mb-3">
                                                    <h6 className="border-bottom pb-2">{reason}</h6>
                                                    {changes.map(change => (
                                                        <div key={change.ID} className="mb-2 ms-3 change-order-task-change-option">
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`change-${change.ID}`}
                                                                label={change.Title || "Untitled"}
                                                                onChange={(e) => handleChangeSelect(e, change.ID)}
                                                                checked={selectedChanges.includes(change.ID)}
                                                            />
                                                            <div className="ms-4 small text-muted change-order-task-change-description">
                                                                Changed from: {change.ExistingValue || "BLANK"} to: {change.NewValue || "BLANK"}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p>No changes found for this change order</p>
                                    )}
                                </Form.Group>

                                {availableChanges.length > 0 && (
                                    <Form.Group className="mb-3">
                                        <Form.Label>Assign to Departments</Form.Label>
                                        <div className="d-flex gap-3 change-order-department-grid">
                                            <Form.Check
                                                type="checkbox"
                                                id="mechanical-assignment"
                                                label={`Mechanical (${projectDetails[0]?.MechanicalEngineer || "Not assigned"})`}
                                                checked={assignmentData.mechanical}
                                                onChange={(e) => handleAssignmentChange('mechanical', e.target.checked)}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="electrical-assignment"
                                                label={`Electrical (${projectDetails[0]?.ElectricalEngineer || "Not assigned"})`}
                                                checked={assignmentData.electrical}
                                                onChange={(e) => handleAssignmentChange('electrical', e.target.checked)}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="controls-assignment"
                                                label={`Controls (${projectDetails[0]?.ControlsEngineer || "Not assigned"})`}
                                                checked={assignmentData.controls}
                                                onChange={(e) => handleAssignmentChange('controls', e.target.checked)}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                id="csm-assignment"
                                                label="CSM"
                                                checked={assignmentData.csm}
                                                onChange={(e) => handleAssignmentChange('csm', e.target.checked)}
                                            />
                                        </div>
                                    </Form.Group>
                                )}
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAddTaskModal}>
                        Cancel
                    </Button>
                    {/* Preview button that shows before assigning */}
                    <Button
                        variant="info"
                        onClick={generateEmailPreview}
                        disabled={selectedChanges.length === 0 || (!assignmentData.mechanical && !assignmentData.electrical && !assignmentData.controls && !assignmentData.csm)}
                    >
                        Preview Notifications
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignTasks}
                        disabled={selectedChanges.length === 0 || (!assignmentData.mechanical && !assignmentData.electrical && !assignmentData.controls && !assignmentData.csm) || sendingEmails}
                    >
                        {sendingEmails ? "Sending Notifications..." : "Assign Tasks"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Email Preview Modal */}
            <Modal show={isPreviewMode} onHide={() => setIsPreviewMode(false)} size="lg" className="change-order-modal change-order-task-preview-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Email Notification Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-4">
                        <h5>Recipients:</h5>
                        {emailPreview.recipients.length > 0 ? (
                            <ul className="list-group">
                                {emailPreview.recipients.map((recipient, index) => (
                                    <li key={index} className="list-group-item d-flex justify-content-between align-items-center change-order-recipient-item">
                                        <div>
                                            <strong>{recipient.name}</strong> - {recipient.department}
                                            <div className="text-muted small">{recipient.email}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="alert alert-warning">
                                No recipients found. Make sure at least one department has an assigned engineer.
                            </div>
                        )}
                    </div>

                    <div className="mb-4">
                        <h5>CC Recipients:</h5>
                        <ul className="list-group">
                            {CC_RECIPIENTS.map((email, index) => (
                                <li key={index} className="list-group-item">
                                    {email}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="mb-4">
                        <h5>Subject:</h5>
                        <div className="p-2 border rounded">{emailPreview.subject}</div>
                    </div>

                    <div>
                        <h5>Email Content:</h5>
                        <div className="p-3 border rounded bg-light">
                            <div dangerouslySetInnerHTML={{ __html: emailPreview.body }}></div>
                        </div>
                    </div>

                    {/* Admin-only self-test option */}
                    {roles.Admin && (
                        <div className="mt-4">
                            <Form.Check
                                type="checkbox"
                                id="self-test-mode"
                                label="Self-Test Mode (send all notifications only to me)"
                                checked={selfTestMode}
                                onChange={(e) => setSelfTestMode(e.target.checked)}
                            />
                            {selfTestMode && (
                                <div className="text-muted small mt-1">
                                    All notifications will be sent only to {userDetails?.name} ({userDetails?.username})
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setIsPreviewMode(false)}>
                        Back to Edit
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAssignTasks}
                        disabled={sendingEmails || emailPreview.recipients.length === 0}
                    >
                        {sendingEmails ? "Sending Notifications..." : "Confirm & Send"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {localChangeOrderDetails.map((detail, index) => {
                const assignedPerson = getAssignedPerson(detail);
                const acknowledgedField = getAcknowledgedField(detail);
                const completedField = getCompletedField(detail);
                const parentOrder = changeOrderParent.find(order => order.ChangeOrderNumber === detail.ChangeOrderNumber);
                const acknowledged = detail[acknowledgedField];
                const completed = detail[completedField];

                return (
                    <Form key={index} className="mb-4 p-3 border rounded position-relative change-order-task-card">
                        <Row className="align-items-center mb-2 change-order-task-header-grid">
                            <Col md={4} className="change-order-task-title">
                                <Form.Label className="change-order-responsive-label">Task</Form.Label>
                                <Form.Control
                                    type="text"
                                    disabled="disabled"
                                    value={detail.Title}
                                    onChange={(e) => handleInputChange(detail.ID, 'Title', e.target.value)}
                                    onBlur={(e) => handleInputChange(detail.ID, 'Title', e.target.value)}
                                />
                            </Col>
                            <Col md={4} className="change-order-task-assignee">
                                <span>Assigned to: {assignedPerson}</span>
                            </Col>
                            <Col md={2} className="change-order-task-check change-order-task-acknowledged">
                                <label className="form-check change-order-checkbox-tile" htmlFor={`acknowledged-${detail.ID}-${detail.actionType}`}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`acknowledged-${detail.ID}-${detail.actionType}`}
                                        checked={acknowledged}
                                        onChange={(e) => handleCheckboxChange(detail.ID, acknowledgedField, e.target.checked)}
                                        disabled={!canModifyTask(detail)}
                                    />
                                    <span className="form-check-label">Acknowledged</span>
                                </label>
                            </Col>
                            <Col md={2} className="change-order-task-check change-order-task-completed">
                                <label className="form-check change-order-checkbox-tile" htmlFor={`completed-${detail.ID}-${detail.actionType}`}>
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`completed-${detail.ID}-${detail.actionType}`}
                                        checked={completed}
                                        disabled={!acknowledged || !canModifyTask(detail)}
                                        onChange={(e) => handleCheckboxChange(detail.ID, completedField, e.target.checked)}
                                    />
                                    <span className="form-check-label">Completed</span>
                                </label>
                            </Col>
                        </Row>
                        <div style={{ height: '5px', ...getColorBarStyle(acknowledged, completed) }}></div>
                        <Row className="align-items-center mb-2 mt-2 border change-order-task-description-row">
                            <Col>
                                <p>
                                    {detail.Title} {detail.Field} <span style={{ color: 'red' }}>changed from</span> {detail.ExistingValue} <span style={{ color: 'red' }}>to</span> {detail.NewValue}
                                </p>
                            </Col>
                        </Row>
                        <Row className="justify-content-end change-order-task-footer-row">
                            <Col md="auto">
                                CO# {detail.ChangeOrderNumber} -  {parentOrder ? formatDate(parentOrder.DateSubmitted) : ''}
                            </Col>
                        </Row>
                    </Form>
                );
            })}
        </Container>
    );
};

export default ChangeOrderTasks;
